import React, { useState } from 'react';
import { Logo } from './Logo';
import { useQuote } from '../context/QuoteContext';
import { buildCleanPath } from '../utils/navigation';
import { Menu, X, ShoppingBag, PhoneCall, ShieldCheck } from 'lucide-react';

interface NavbarProps {
  currentPage: string;
  onNavigate: (page: string, productId?: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ currentPage, onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { totalCount, setIsDrawerOpen } = useQuote();

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'products', label: 'Products' },
    { id: 'services', label: 'Servicing & Spares' },
    { id: 'blog', label: 'Blog & Insights' },
    { id: 'about', label: 'About Us' },
    { id: 'contact', label: 'Contact' },
  ];

  const handleLinkClick = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    onNavigate(id);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Top micro-utility notice bar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-1.5 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-sky-400 font-medium">
              <ShieldCheck className="w-3.5 h-3.5" />
              KeBS Aligned & Food-Grade SS304/316
            </span>
            <span className="hidden sm:inline text-slate-500">|</span>
            <span className="hidden sm:inline text-slate-300">Juja Road 478/B Green Mall, Nairobi</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="tel:+254727072393"
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-white font-medium transition-colors"
            >
              <PhoneCall className="w-3 h-3 text-emerald-400" />
              <span className="tabular-nums">+254 727 072 393</span>
            </a>
            <span className="hidden md:inline text-slate-400">Mon–Fri: 9am–5pm</span>
          </div>
        </div>
      </div>

      {/* Main Top Bar strictly fulfilling 3-zone contract */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-8 h-20">
            {/* Zone 1: Single text/brand wordmark */}
            <a
              href="/"
              onClick={(e) => handleLinkClick('home', e)}
              className="text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-500 rounded-md"
            >
              <Logo size="md" variant="horizontal" />
            </a>

            {/* Zone 2: Concise single-line navigation links */}
            <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600">
              {navLinks.map((link) => {
                const isActive = currentPage === link.id;
                return (
                  <a
                    key={link.id}
                    href={buildCleanPath(link.id)}
                    onClick={(e) => handleLinkClick(link.id, e)}
                    className={`whitespace-nowrap shrink-0 transition-colors py-1 relative ${
                      isActive
                        ? 'text-sky-700 font-semibold'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 rounded-full" />
                    )}
                  </a>
                );
              })}
            </nav>

            {/* Zone 3: 1 Primary Action (RFQ Quote Drawer & Mobile Toggle) */}
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="relative inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-700 active:bg-sky-800 rounded-lg shadow-sm transition-colors whitespace-nowrap shrink-0 cursor-pointer focus:ring-2 focus:ring-sky-500 focus:outline-hidden"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>Request Quote</span>
                {totalCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.2 bg-rose-600 text-white rounded-full text-[10px] font-bold tabular-nums">
                    {totalCount}
                  </span>
                )}
              </button>

              {/* Mobile menu trigger */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg focus:outline-hidden"
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 pt-3 pb-6 space-y-2 shadow-lg animate-fadeIn">
            {navLinks.map((link) => {
              const isActive = currentPage === link.id;
              return (
                <a
                  key={link.id}
                  href={buildCleanPath(link.id)}
                  onClick={(e) => handleLinkClick(link.id, e)}
                  className={`block w-full text-left px-3 py-2.5 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-sky-50 text-sky-700 font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <a
                href="tel:+254727072393"
                className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-semibold text-slate-800 bg-slate-100 rounded-lg"
              >
                <PhoneCall className="w-4 h-4 text-emerald-600" />
                Call +254 727 072 393
              </a>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setIsDrawerOpen(true);
                }}
                className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-semibold text-white bg-sky-600 rounded-lg"
              >
                <ShoppingBag className="w-4 h-4" />
                Open Quote Basket ({totalCount})
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
